import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-6 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start justify-between mb-4">
            <Button 
              onClick={() => navigate('/')} 
              variant="ghost" 
              className="text-white hover:text-[#d97706]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <a href="tel:4258902558" className="text-xl font-bold text-white hover:text-[#d97706] transition-colors" data-testid="privacy-header-phone-link">
              425.890.2558
            </a>
          </div>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-gray-300 mt-2">Last Updated: January 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Diamond Trucking School, Inc. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Submit an inquiry form or contact us</li>
              <li>Register for training programs</li>
              <li>Communicate with us via phone, email, or in person</li>
              <li>Apply for financial assistance or payment plans</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              This information may include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Name, email address, and phone number</li>
              <li>Mailing address</li>
              <li>Driver's license information</li>
              <li>Payment and billing information</li>
              <li>Educational background and training history</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and cookies installed on your device.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Process your enrollment in training programs</li>
              <li>Communicate with you about classes, schedules, and important updates</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Process payments and maintain financial records</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve our website and services</li>
              <li>Send you marketing communications (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Sharing Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Service Providers:</strong> Third-party vendors who assist us in operating our business (payment processors, email services, etc.)</li>
              <li><strong>Regulatory Authorities:</strong> Government agencies as required by law for licensing and certification purposes</li>
              <li><strong>Business Partners:</strong> Potential employers or trucking companies for job placement assistance (with your consent)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Access and receive a copy of your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent for data processing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Cookies and Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Diamond Trucking School, Inc.</strong></p>
              <p className="text-gray-700 mb-2">8217 44th Ave W. Bldg. F, Suite B</p>
              <p className="text-gray-700 mb-2">Mukilteo, WA 98275</p>
              <p className="text-gray-700 mb-2">Phone: (425) 890-2558</p>
              <p className="text-gray-700">Email: diamondtruckingschool@gmail.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
